/**
 * Конфигурация навигации сайдбара CRM.
 *
 * Меню строится из ключей разделов, которые отдал сервер (DEV-1256, ADR-0042).
 * До реформы здесь лежал интерфейс NavPermissions из 16 ролевых флагов — второй
 * источник правды о доступе, расходившийся и с роутером, и с бэкендом.
 *
 * Функция чистая: что показать, решает вход `allowed`, а как это выглядит —
 * реестр разделов плюс карты иконок и групп ниже. Поэтому структуру меню
 * (группы, дубли, порядок) проверяет тест без монтирования компонента.
 */
import {
  HomeIcon,
  UsersIcon,
  BriefcaseIcon,
  BuildingIcon,
  Building2Icon,
  FolderInputIcon,
  ListIcon,
  CalendarIcon,
  BellIcon,
  UserIcon,
  HandshakeIcon,
  InboxIcon,
  ShieldCheckIcon,
  UserCogIcon,
  TrendingUpIcon,
  BarChartIcon,
  PhoneIcon,
  FileTextIcon,
  SettingsIcon,
  RouteIcon,
  ShieldIcon,
  SlidersHorizontalIcon,
  UserPlusIcon,
  UsersRoundIcon,
  LayoutListIcon,
  ScrollTextIcon,
} from 'lucide-vue-next'

import {
  isKnownSectionKey,
  SECTION_REGISTRY,
  type SectionGroup,
  type SectionKey,
} from '@/features/auth/sections/registry'

export interface NavItem {
  /** Отсутствует у групп-аккордеонов: такой пункт только раскрывает подпункты. */
  path?: string
  label: string
  icon?: object
  badge?: string | number
  /** alert — бейдж горит красным (есть просрочка, требующая реакции). */
  badgeVariant?: 'alert'
  children?: NavItem[]
}

export interface NavSection {
  id: string
  title?: string
  items: NavItem[]
}

export interface NavBadges {
  newClients?: number
  newDeals?: number
  totalProperties?: number
  pendingVerifications?: number
  /** Просроченные заявки модерации: красят бейдж в алерт (DEV-1165). */
  overdueVerifications?: number
}

type Translate = (key: string) => string

const SECTION_ICONS: Record<SectionKey, object> = {
  dashboard: HomeIcon,
  clients: UsersIcon,
  deals: BriefcaseIcon,
  // Не BuildingIcon: он у группы «Управление проектами», и одинаковая иконка
  // у группы и её первого подпункта читается как дубль.
  properties: ListIcon,
  properties_import: FolderInputIcon,
  calendar: CalendarIcon,
  agents: UsersIcon,
  agent_tickets: InboxIcon,
  company_verifications: ShieldCheckIcon,
  agents_admin: UserCogIcon,
  partner_companies: Building2Icon,
  reports: BarChartIcon,
  rc_analytics: TrendingUpIcon,
  rc_moderation: ShieldCheckIcon,
  calls: PhoneIcon,
  documents: FileTextIcon,
  routing_rules: RouteIcon,
  // DEV-1267. Админ-раздел: люди и приглашения. UsersIcon занят «Клиентами»,
  // поэтому у сотрудников своя иконка — иначе два пункта меню читаются как один.
  admin_users: UsersRoundIcon,
  admin_invitations: UserPlusIcon,
  // DEV-1268. Ролевая модель: роли с правами, реестр разделов, журнал.
  admin_roles: ShieldIcon,
  admin_sections: LayoutListIcon,
  admin_audit: ScrollTextIcon,
  pipeline: SlidersHorizontalIcon,
  settings: SettingsIcon,
  // Пункта в меню у этих двух нет (см. HIDDEN_IN_SIDEBAR), но реестр обязан
  // покрывать все ключи целиком: так новый раздел нельзя забыть.
  notifications: BellIcon,
  profile: UserIcon,
}

/**
 * Разделы без собственного пункта в меню: «Уведомления» и «Кабинет» доступны из
 * шапки — колокольчик и меню пользователя. В реестре они есть, потому что
 * реестр отвечает ещё и за гард маршрутов.
 *
 * ⚠️ Макет 737:2 рисует их в подвале сайдбара, а не в шапке, и подписывает
 * «Уведомления и профиль — не разделы, приходят вне карты». Перенос затрагивает
 * DashboardLayout целиком, поэтому вынесен из этого тикета отдельно.
 */
const HIDDEN_IN_SIDEBAR: ReadonlySet<SectionKey> = new Set(['notifications', 'profile'])

/** Группы-аккордеоны главного меню. Собственной страницы у них нет. */
const GROUP_META: Partial<Record<SectionGroup, { labelKey: string; icon: object }>> = {
  // DEV-1166. Импорт из Drive — такая же часть работы с проектами, как их
  // список, и искать его в другом месте меню незачем.
  projects: { labelKey: 'common.navigation.projectsSection', icon: BuildingIcon },
  // Реестр (DEV-476), обращения (DEV-436), приём компаний (DEV-795) и
  // назначение кураторов (DEV-437) — один раздел, не четыре.
  agents: { labelKey: 'common.navigation.agents', icon: HandshakeIcon },
  analytics: { labelKey: 'common.navigation.analytics', icon: TrendingUpIcon },
  // «Настройки» — не футер, а пункт основного меню со своей страницей и
  // подпунктом-конструктором (макет 737:2). В футере остаётся только тема.
  settings: { labelKey: 'common.navigation.settings', icon: SettingsIcon },
  // DEV-1267. Управление людьми и настройка системы: пользователи,
  // маршрутизация лидов (DEV-352), конструктор воронки (DEV-522).
  //
  // До этого группа уезжала подпунктами «Настроек» в футер. Для конструкторов
  // это годилось (макет 621:2 «Сайдбар v2»), но «Пользователей» такое соседство
  // делает настройкой профиля: администрирование людей — не личная настройка.
  // Поэтому «Настройки» в футере остаются про себя (профиль, тема), а всё
  // системное собрано здесь.
  admin: { labelKey: 'common.navigation.administration', icon: ShieldIcon },
}

const count = (value?: number): string | undefined => (value ? String(value) : undefined)

/**
 * Группа с единственным подпунктом бессмысленна — показываем сам подпункт.
 * Исключение — группа с собственной страницей («Настройки»): схлопнуть её
 * значило бы потерять саму страницу, оставив в меню только конструктор.
 */
const collapse = (item: NavItem): NavItem[] => {
  if (item.path) return [item]
  const children = item.children ?? []
  if (children.length === 0) return []
  const [only] = children
  if (children.length === 1 && only) return [only]
  return [item]
}

const flatten = (items: NavItem[]): NavItem[] =>
  items.flatMap((item) => [item, ...flatten(item.children ?? [])])

/**
 * Активен ровно один пункт — самый специфичный из подходящих. Без этого
 * `/dashboard/agents-admin` подсвечивал заодно `/dashboard/agents`, а
 * `/dashboard/rc-analytics/moderation` — «Аналитику ЖК».
 */
export const resolveActivePath = (items: NavItem[], routePath: string): string | undefined =>
  flatten(items)
    .map((i) => i.path)
    .filter((p): p is string => {
      if (!p) return false
      // «Дашборд» — префикс всех разделов, поэтому активен только на самом себе.
      if (p === '/dashboard') return routePath === p
      return routePath === p || routePath.startsWith(`${p}/`)
    })
    .sort((a, b) => b.length - a.length)[0]

const buildItem = (key: SectionKey, t: Translate, badges: NavBadges): NavItem => {
  const item: NavItem = {
    path: SECTION_REGISTRY[key].path,
    label: t(SECTION_REGISTRY[key].labelKey),
    icon: SECTION_ICONS[key],
  }

  if (key === 'clients') item.badge = count(badges.newClients)
  if (key === 'deals') item.badge = count(badges.newDeals)
  if (key === 'properties') item.badge = count(badges.totalProperties)
  if (key === 'company_verifications') {
    // Просрочка перебивает обычный счётчик очереди: модератор должен увидеть
    // проблему из меню, не заходя в раздел (DEV-1165). Просроченной может быть и
    // заявка вне очереди действий (например ai_no_result) — тогда показываем её.
    item.badge = count(badges.pendingVerifications || badges.overdueVerifications)
    if (badges.overdueVerifications) item.badgeVariant = 'alert'
  }

  return item
}

/**
 * @param allowed ключи разделов в порядке, который задал сервер. Порядок пунктов
 *   меню — этот же; группа встаёт на место своего первого подпункта, поэтому
 *   перестановка разделов на бэкенде не требует релиза фронта.
 */
export const buildSidebarNav = (
  allowed: readonly string[],
  t: Translate,
  badges: NavBadges = {},
): { sections: NavSection[]; settings: NavItem | null } => {
  const items: NavItem[] = []
  const groups = new Map<SectionGroup, NavItem>()

  /**
   * Заводит аккордеон группы на её месте в порядке сервера. Раздел, который сам
   * является головой группы («Настройки»), приносит сюда свой маршрут — тогда
   * заголовок кликабельный и ведёт на страницу, а шеврон раскрывает подпункты.
   */
  const accordion = (group: SectionGroup, head?: NavItem): NavItem | undefined => {
    const meta = GROUP_META[group]
    if (!meta) return undefined

    let item = groups.get(group)
    if (!item) {
      item = { label: t(meta.labelKey), icon: meta.icon, children: [] }
      groups.set(group, item)
      items.push(item)
    }
    if (head) Object.assign(item, { path: head.path, label: head.label, icon: head.icon })
    return item
  }

  for (const key of allowed) {
    // Ключ из будущей версии бэкенда: пропускаем, но громко — иначе раздел
    // молча исчезнет из меню и это спишут на права.
    if (!isKnownSectionKey(key)) {
      console.warn(`[sidebarNav] неизвестный ключ раздела: ${key}`)
      continue
    }
    if (HIDDEN_IN_SIDEBAR.has(key)) continue

    const item = buildItem(key, t, badges)
    const group = SECTION_REGISTRY[key].group

    // «Настройки» — сами себе голова группы: страница у них своя, а подпунктом
    // висит конструктор воронки.
    if (key === 'settings') {
      if (!accordion('settings', item)) items.push(item)
      continue
    }

    if (!group || !GROUP_META[group]) {
      items.push(item)
      continue
    }

    accordion(group)?.children?.push(item)
  }

  // Футер сайдбара — только переключатель темы: «Настройки» с DEV-1267 стоят
  // пунктом основного меню, как в макете 737:2.
  const settings: NavItem | null = null

  const topLevel = items.flatMap((item) => (item.children ? collapse(item) : [item]))

  return {
    sections: [{ id: 'main', items: topLevel }].filter((s) => s.items.length > 0),
    settings,
  }
}
